import { ActionReducerMapBuilder, createAsyncThunk } from "@reduxjs/toolkit";
import { graphqlRequest } from "context/graphql/functions";
import { initializePartnerVisit } from "helpers/initializeJobVisit";
import { isEqual } from "lodash";
import { GetMachinesQueryVariables } from "operations/schema/schema";
import { AppAsyncThunkConfig } from "store";
import { State } from "./partner.store";

export const createAppAsyncThunk = createAsyncThunk.withTypes<AppAsyncThunkConfig>();

export const asyncQueries = {
  getPartnerJob: createAppAsyncThunk(
    "partner/getPartnerJob",
    async (jobId: string, { getState, rejectWithValue, extra: { sdk } }) => {
      const { data, errors } = await graphqlRequest(sdk.getJob, {
        variables: {
          id: jobId,
        },
      });
      if (errors) return rejectWithValue(errors);
      if (!data?.job) return rejectWithValue("something went wrong");
      return {
        data,
        selectedJobId: data.job.id,
      };
    }
  ),

  refreshJob: createAppAsyncThunk(
    "partner/refreshJob",
    async (_, { getState, rejectWithValue, extra: { sdk } }) => {
      const {
        partner: { selectedJobId, token },
      } = getState();
      if (!selectedJobId || !token) return rejectWithValue("No selected job");
      const { data, errors } = await graphqlRequest(sdk.refreshJob, {
        variables: {
          jobId: selectedJobId,
        },
      });
      if (errors) return rejectWithValue(errors);
      if (!data?.job || !data?.files || !data?.workNotes)
        return rejectWithValue("something went wrong");
      return {
        data,
        selectedJobId,
      };
    }
  ),

  getFiles: createAppAsyncThunk(
    "partner/getFiles",
    async (_, { getState, rejectWithValue, extra: { sdk } }) => {
      const {
        partner: { token, selectedJobId },
      } = getState();
      if (!token || !selectedJobId) return rejectWithValue("No selected job");
      const { data, errors } = await graphqlRequest(sdk.getFiles, {
        variables: {
          jobId: selectedJobId,
        },
      });
      if (errors) return rejectWithValue(errors);
      if (!data?.files) return rejectWithValue("something went wrong");
      return { data, selectedJobId };
    }
  ),
  getNotes: createAppAsyncThunk(
    "partner/getNotes",
    async (_, { getState, rejectWithValue, extra: { sdk } }) => {
      const {
        partner: { token, selectedJobId },
      } = getState();
      if (!token || !selectedJobId) return rejectWithValue("No selected job");
      const { data, errors } = await graphqlRequest(sdk.getWorkNotes, {
        variables: {
          jobId: selectedJobId,
        },
      });
      if (errors) return rejectWithValue(errors);
      if (!data?.workNotes) return rejectWithValue("something went wrong");
      return { data, selectedJobId };
    }
  ),
  getVisits: createAppAsyncThunk(
    "partner/getVisits",
    async (_, { getState, rejectWithValue, extra: { sdk } }) => {
      const {
        partner: { token, selectedJobId },
      } = getState();
      if (!token || !selectedJobId) return rejectWithValue("No selected job");
      const { data, errors } = await graphqlRequest(sdk.getJobVisits, {
        variables: {
          max: 20,
          jobId: selectedJobId,
        },
      });
      if (errors) return rejectWithValue(errors);
      if (!data?.jobVisits) return rejectWithValue("something went wrong");
      return { data, selectedJobId };
    }
  ),

  //ChangeJobMachineDialog
  getMachines: createAppAsyncThunk(
    "partner/getMachines",
    async (variables: GetMachinesQueryVariables, { rejectWithValue, extra: { sdk } }) => {
      const { data, errors } = await graphqlRequest(sdk.getMachines, {
        variables,
      });
      if (errors) return rejectWithValue(errors);
      if (!data?.machines) return rejectWithValue("something went wrong");
      return data;
    }
  ),
  //Contacts
  getContacts: createAppAsyncThunk(
    "partner/getContacts",
    async (_, { rejectWithValue, getState, extra: { sdk } }) => {
      const {
        partner: { jobs, selectedJobId, token },
      } = getState();
      if (!token || !selectedJobId) return rejectWithValue("No selected job");
      const { customer } = jobs[selectedJobId];
      if (!customer || !customer.id) return rejectWithValue("No customer");
      const { data, errors } = await graphqlRequest(sdk.getContacts, {
        variables: {
          customerId: customer?.id,
        },
      });
      if (errors) return rejectWithValue(errors);
      if (!data?.contacts) return rejectWithValue("something went wrong");
      return data.contacts;
    }
  ),
};

export const queryBuilder = (builder: ActionReducerMapBuilder<State>) => {
  builder.addCase(asyncQueries.getPartnerJob.pending, (state) => {
    state.loadingJob = true;
    return state;
  });
  builder.addCase(asyncQueries.getPartnerJob.rejected, (state) => {
    state.loadingJob = false;
    return state;
  });
  builder.addCase(
    asyncQueries.getPartnerJob.fulfilled,
    (state, { payload: { data, selectedJobId } }) => {
      let { job } = data;
      if (!selectedJobId || !job) return state;
      state.selectedJobId = selectedJobId;

      if (!state.jobs[selectedJobId]) {
        state.jobs[selectedJobId] = {
          ...job,
          files: [],
          workNotes: [],
          visits: [],
        };
        initializePartnerVisit(state, state.jobs[selectedJobId]);
      } else {
        state.jobs[selectedJobId] = {
          ...state.jobs[selectedJobId],
          ...job,
          files: state.jobs[selectedJobId].files || [],
          workNotes: state.jobs[selectedJobId].workNotes || [],
          visits: state.jobs[selectedJobId].visits || [],
        };
        if (!state.jobVisits[selectedJobId]) {
          initializePartnerVisit(state, state.jobs[selectedJobId]);
        }
      }

      state.loadingJob = false;
      return state;
    }
  );
  builder.addCase(asyncQueries.refreshJob.pending, (state) => {
    state.loadingJob = true;
    return state;
  });
  builder.addCase(asyncQueries.refreshJob.rejected, (state) => {
    state.loadingJob = false;
    return state;
  });
  builder.addCase(
    asyncQueries.refreshJob.fulfilled,
    (state, { payload: { data, selectedJobId } }) => {
      let { job } = data;
      if (!selectedJobId || !job || !state.jobs[selectedJobId]) return state;
      const oldFiles = state.jobs[selectedJobId].files;
      const oldPreorderedParts = state.jobs[selectedJobId].preOrderedParts;
      const oldSymptomText = state.jobs[selectedJobId].symptomDescription;
      const oldInternalNotes = state.jobs[selectedJobId].workNotes;
      state.jobs[selectedJobId] = {
        ...state.jobs[selectedJobId],
        ...job,
        files: [...data.files],
        workNotes: [...data.workNotes],
        visits: state.jobs[selectedJobId].visits || [],
      };
      state.refreshJobNotifications.symptomText = !isEqual(
        oldSymptomText,
        data.job?.symptomDescription
      );
      state.refreshJobNotifications.internalNotes =
        data.workNotes.length !== oldInternalNotes.length ||
        !isEqual(oldInternalNotes, data.workNotes);
      state.refreshJobNotifications.files =
        data.files.length !== oldFiles.length || !isEqual(oldFiles, data.files);
      state.refreshJobNotifications.preOrderedParts =
        data.job?.preOrderedParts.length !== oldPreorderedParts.length ||
        !isEqual(oldPreorderedParts, data.job.preOrderedParts);

      if (!state.jobVisits[job.id]) {
        initializePartnerVisit(state, state.jobs[selectedJobId]);
      }
      state.loadingJob = false;
      return state;
    }
  );

  builder.addCase(
    asyncQueries.getFiles.fulfilled,
    (state, { payload: { data, selectedJobId } }) => {
      if (!selectedJobId) return state;
      state.jobs[selectedJobId].files = [...data.files];
      return state;
    }
  );
  builder.addCase(
    asyncQueries.getNotes.fulfilled,
    (state, { payload: { data, selectedJobId } }) => {
      if (!selectedJobId) return state;
      state.jobs[selectedJobId].workNotes = [...data.workNotes];
      return state;
    }
  );
  builder.addCase(
    asyncQueries.getVisits.fulfilled,
    (state, { payload: { data, selectedJobId } }) => {
      if (!selectedJobId) return state;
      state.jobs[selectedJobId].visits = [...data.jobVisits];
      return state;
    }
  );
  // ChangeJobMachineDialog
  builder.addCase(asyncQueries.getMachines.pending, (state) => {
    state.changeJobMachine.loadingMachines = true;
    return state;
  });
  builder.addCase(asyncQueries.getMachines.rejected, (state) => {
    state.changeJobMachine.loadingMachines = false;
    return state;
  });
  builder.addCase(asyncQueries.getMachines.fulfilled, (state, { payload: { machines } }) => {
    state.changeJobMachine.loadingMachines = false;
    state.changeJobMachine.machines = [...machines];
    return state;
  });
  // Contacts
  builder.addCase(asyncQueries.getContacts.rejected, (state, { payload: errors }) => {
    state.contactsLoading = false;
    return state;
  });
  builder.addCase(asyncQueries.getContacts.pending, (state) => {
    state.contactsLoading = true;
    return state;
  });
  builder.addCase(asyncQueries.getContacts.fulfilled, (state) => {
    state.contactsLoading = false;
    return state;
  });
};
